import { Button, DatePicker, Flex } from '@utilitywarehouse/hearth-react';
import dayjs from 'dayjs';
import { FormikProps, useFormikContext } from 'formik';
import { Ref } from 'react';

import { useFieldProps } from '@hooks/useFieldProps';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { DateFormat, MonthPickerFormat } from '@src/types/index';
import {
  ContractRenewalFormValues,
  YourDetailsFormValues,
} from '@src/types/yourDetails';

const now = dayjs();

interface RenewalCaptureFormProps {
  field: keyof ContractRenewalFormValues;
  label: string;
  handleClear?: () => void;
  loading?: boolean;
  ref?: Ref<HTMLDivElement>;
}

const RenewalCaptureForm = ({
  field,
  label,
  loading,
  handleClear = () => {},
  ref,
}: RenewalCaptureFormProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
  }: FormikProps<YourDetailsFormValues> = useFormikContext();
  const { canUpdateState } = useAppointmentContext();
  const { setDetailsItem } = useDetailsContext();

  const handleDateChange = (date: Date | null) => {
    if (!canUpdateState) {
      return;
    }

    const convertedDate = dayjs(date).startOf('month');

    if (convertedDate.isValid()) {
      const dateString = convertedDate.format(DateFormat.ISO8601);
      setFieldValue(field, dateString);
      setDetailsItem(field, dateString);
    } else {
      handleClear();
    }
  };

  const clearDate = () => {
    setFieldValue(field, undefined);

    handleClear();
  };

  const getFieldProps = useFieldProps<YourDetailsFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const { value, validationStatus, validationText, id, name } =
    getFieldProps(field);

  const isReadOnly = !canUpdateState;
  // we need to convert the value to a Date object to
  // satisfy the date picker. It should, however, be stored
  // in state as a string to facilitate syncing.
  // Variable will default to `null` if no `value`, to prevent
  // dayjs itself defaulting to `new Date()`
  const dateObjectValue = value ? dayjs(value).toDate() : null;

  return (
    <div ref={ref}>
      <Flex
        className="renewalCapture__container"
        alignItems={{ tablet: 'end', mobile: 'stretch' }}
        direction={{ tablet: 'row', mobile: 'column' }}
        spacing="lg"
      >
        <DatePicker
          id={id}
          name={name}
          className="renewalCapture__date-picker"
          onChange={handleDateChange}
          placeholderText={MonthPickerFormat.Display}
          dateFormat={MonthPickerFormat.Display}
          label={label}
          showMonthYearPicker
          minDate={now.toDate()}
          selected={dateObjectValue}
          disabled={loading || isReadOnly}
          validationText={validationText}
          validationStatus={validationStatus}
          required
        />
        <Flex direction="column">
          {values[field] && (
            <Button
              type="button"
              size="md"
              variant="outline"
              colorScheme="functional"
              loading={loading}
              disabled={isReadOnly}
              onClick={clearDate}
            >
              Clear
            </Button>
          )}
        </Flex>
      </Flex>
    </div>
  );
};
export { RenewalCaptureForm };
